/**
 * Positive proof that a platform actually accepted a post.
 *
 * The product bar: Mora must NEVER tell a user their content published unless
 * the platform handed back a real, platform-shaped post id. A publish call that
 * returns 2xx with no usable id is an UNKNOWN outcome, not a success — the post
 * may or may not exist, and we cannot fetch, verify, or measure it.
 *
 * This exists because the LinkedIn client used to substitute the literal string
 * 'UNKNOWN_ID' when the `x-restli-id` header was missing, which flowed all the
 * way into `social_posts.platform_response` as `{status:'success'}` and rendered
 * a green "Published" check against a post nobody could ever locate.
 *
 * Validation is deliberately SHAPE-based, not existence-based: it proves the
 * platform returned something id-shaped, not that the content is live. Proving
 * liveness requires a read-back against the platform API, which is a separate
 * concern (see the verification sweep).
 */

/** Instagram/Facebook numeric media + page-post ids (`17841…`, `1234_5678`). */
const META_ID = /^\d{5,}(_\d+)?$/;
/** LinkedIn share/ugcPost/activity URNs. */
const LINKEDIN_URN = /^urn:li:(share|ugcPost|activity):\d+$/;
/** Pinterest pin ids are numeric strings. */
const PINTEREST_ID = /^\d{5,}$/;

/**
 * Values that are technically non-empty strings but carry no information.
 * Anything here is treated as "no id at all".
 */
const SENTINEL_IDS = new Set([
  'unknown_id',
  'unknown',
  'null',
  'undefined',
  'none',
  'n/a',
  'na',
  '0',
  '-1',
  'true',
  'false',
]);

export function isSentinelId(id: string): boolean {
  return SENTINEL_IDS.has(id.trim().toLowerCase());
}

/**
 * Whether `id` is a credible post id for `platform`.
 *
 * Unknown platforms fall back to a permissive non-empty/non-sentinel check so
 * that adding a new platform client can never silently regress into "no id is
 * fine" — it still rejects the sentinel class that caused the original bug.
 */
export function isValidPlatformPostId(platform: string, id: unknown): boolean {
  if (typeof id !== 'string') return false;
  const trimmed = id.trim();
  if (!trimmed || isSentinelId(trimmed)) return false;

  switch (platform) {
    case 'instagram':
    case 'facebook':
      return META_ID.test(trimmed);
    case 'linkedin':
      return LINKEDIN_URN.test(trimmed);
    case 'pinterest':
      return PINTEREST_ID.test(trimmed);
    // TikTok publishes asynchronously: the PUBLIC post id is proof, the
    // ephemeral publish_id is not. The client is responsible for handing us the
    // public id; anything shorter than a plausible id is rejected here.
    case 'tiktok':
      return trimmed.length >= 8;
    default:
      return trimmed.length >= 4;
  }
}
